import { cs } from "../../utils/currency";
export interface ReceiptOrderItem {
  itemId: string;
  name: string;
  quantity: number;
  price: number;
  tax?: number;
  gst?: number;
  pst?: number;
  discountAmount?: number;
  itemstatus?: string;
  customOptions?: { label: string; price: number; qty: number }[];
}

export interface ReceiptOrderMetadata {
  pickup_name?: string;
  pickup_address?: string;
  pickup_phone_number?: string;
  dropoff_name?: string;
  dropoff_phone_number?: string;
}

export interface ReceiptOrder {
  _id: string;
  orderNumber: string;
  invoiceNumber?: string;
  items: ReceiptOrderItem[];
  totalAmount: number;
  restaurantId?: string;
  discountAmount?: number;
  createdAt: string;
  phoneNumber?: string;
  tableToken?: number;
  employeeName?: string;
  payChoice?: string;
  choice?: string;
  tipAmount?: number | string;
  deliveryAmount?: number;
  metadata?: ReceiptOrderMetadata;
  scheduledTimezone?: string;
  isScheduled?: boolean;
  scheduledAt?: string | Date;
  orderBy?: string;
}

/**
 * Money lines for the receipt footer, built so they always add up to the
 * order total. Cancelled items are listed on the receipt but not charged.
 */
export const buildReceiptSummary = (
  order: ReceiptOrder,
  fmt: (value: number) => string = (value) => `${cs()}${value.toFixed(2)}`,
) => {
  const active = order.items.filter((item) => item.itemstatus !== "cancelled");
  const qty = (item: ReceiptOrderItem) => Number(item.quantity || 0);
  const subTotal = active.reduce((s, item) => s + Number(item.price || 0) * qty(item), 0);
  const itemDiscounts = active.reduce((s, item) => s + Number(item.discountAmount || 0), 0);
  const gst = active.reduce((s, item) => s + Number(item.gst || 0) * qty(item), 0);
  const pst = active.reduce((s, item) => s + Number(item.pst || 0) * qty(item), 0);
  // `tax` is the item's whole tax (gst + pst when split), so it is used alone
  const tax = active.reduce((s, item) => s + Number(item.tax || 0) * qty(item), 0);
  const taxTotal = gst + pst > 0 ? gst + pst : tax;
  const discount = itemDiscounts + Number(order.discountAmount || 0);
  const delivery = Number(order.deliveryAmount || 0);
  const tip = Number(order.tipAmount || 0);
  const total = Number(order.totalAmount || 0);
  const round2 = (n: number) => Math.round(n * 100) / 100;
  const adjustment = round2(total - (subTotal - discount + taxTotal + delivery + tip));

  const lines: Array<{ label: string; value: string }> = [
    { label: "Sub Total", value: fmt(subTotal) },
  ];
  if (discount > 0) lines.push({ label: "Discount", value: `- ${fmt(discount)}` });
  if (gst + pst > 0) {
    // Show both lines (PST can legitimately be $0.00, e.g. prepared food in BC)
    lines.push({ label: "GST", value: fmt(gst) });
    lines.push({ label: "PST", value: fmt(pst) });
  } else if (tax > 0) {
    lines.push({ label: "Tax", value: fmt(tax) });
  }
  if (delivery > 0) lines.push({ label: "Delivery", value: fmt(delivery) });
  if (tip > 0) lines.push({ label: "Tip", value: fmt(tip) });
  // Rounding (tax charged on the order vs per item) or an older pricing difference
  if (Math.abs(adjustment) >= 0.01) {
    lines.push({
      label: "Adjustment",
      value: adjustment < 0 ? `- ${fmt(-adjustment)}` : fmt(adjustment),
    });
  }
  return { lines, total };
};

interface GenerateReceiptOptions {
  order: ReceiptOrder;
  logoUrl?: string;
  customerName?: string;
  onPopupBlocked?: () => void;
  currencySymbol?: string;
}

const makeCurrencyFormatter = (symbol: string) =>
  (amount = 0) => `${symbol}${Number(amount).toFixed(2)}`;

// Module-level default (used by ESC/POS functions before symbol is known)
let formatCurrency = makeCurrencyFormatter(cs());

const escapeHtml = (value: unknown) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

export const generateOrderBillPdf = ({
  order,
  logoUrl,
  customerName,
  onPopupBlocked,
  currencySymbol = cs(),
}: GenerateReceiptOptions) => {
  if (typeof window === "undefined") return;
  // Shadow module-level formatter with the correct symbol for this receipt
  const formatCurrency = makeCurrencyFormatter(currencySymbol);

  const billWindow = window.open("", "_blank", "width=420,height=860");
  if (!billWindow) {
    onPopupBlocked?.();
    return;
  }

  const metadata = order.metadata || {};
  const paymentMode = order.payChoice || "";
  const choice = order.choice || "";
  const tz = resolveReceiptTimezone(order);
  const dateStr = new Intl.DateTimeFormat("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
    timeZone: tz,
  }).format(new Date(order.createdAt));
  const timeStr = formatKitchenTime(order.createdAt, tz);

  const summary = buildReceiptSummary(order, formatCurrency);

  const itemsMarkup = order.items
    .map((item) => {
      const lineBase =
        Number(item.price || 0) * Number(item.quantity || 0);
      // Sectioned option display: [WHOLE] / [RIGHT HALF] / [LEFT HALF]
      const wholeH: { label: string; removed: boolean }[] = [];
      const rightH: { label: string; qty: number }[] = [];
      const leftH:  { label: string; qty: number }[] = [];
      (item.customOptions ?? []).forEach((o) => {
        const m = o.label.match(/^(.*?)\s+(left|right|whole)$/i);
        const clean = m ? m[1] : o.label;
        const side  = m ? m[2].toLowerCase() : null;
        if (o.qty === -1)          wholeH.push({ label: clean, removed: true });
        else if (side === "right") rightH.push({ label: clean, qty: o.qty });
        else if (side === "left")  leftH.push({ label: clean, qty: o.qty });
        else                       wholeH.push({ label: clean, removed: false });
      });
      const sectionDivs: string[] = [];
      // Removed items: directly below item name, no section header
      const removedH = wholeH.filter((o) => o.removed);
      const addedWholeH = wholeH.filter((o) => !o.removed);
      removedH.forEach(({ label }) =>
        sectionDivs.push(`<div class="opt-item opt-rm">- ${escapeHtml(label)}</div>`),
      );
      if (addedWholeH.length > 0) {
        const hasHalves = leftH.length > 0 || rightH.length > 0;
        if (hasHalves) sectionDivs.push(`<div class="opt-hdr">[WHOLE]</div>`);
        addedWholeH.forEach(({ label }) =>
          sectionDivs.push(`<div class="opt-item">${escapeHtml(label)}</div>`),
        );
      }
      if (leftH.length > 0) {
        sectionDivs.push(`<div class="opt-hdr">[LEFT HALF]</div>`);
        leftH.forEach(({ label, qty }) =>
          sectionDivs.push(
            `<div class="opt-item">${qty > 1 ? `${qty}x ` : ""}${escapeHtml(label)}</div>`,
          ),
        );
      }
      if (rightH.length > 0) {
        sectionDivs.push(`<div class="opt-hdr">[RIGHT HALF]</div>`);
        rightH.forEach(({ label, qty }) =>
          sectionDivs.push(
            `<div class="opt-item">${qty > 1 ? `${qty}x ` : ""}${escapeHtml(label)}</div>`,
          ),
        );
      }

      const cancelledLabel =
        item.itemstatus === "cancelled" ? " (Cancelled)" : "";

      return `
        <tr>
          <td>${item.quantity}</td>
          <td><strong>${escapeHtml(item.name)}</strong>${cancelledLabel}${
            sectionDivs.length
              ? `<div class="option-wrap">${sectionDivs.join("")}</div>`
              : ""
          }</td>
          <td><strong>${formatCurrency(lineBase)}</strong></td>
        </tr>
      `;
    })
    .join("");

  const html = `
    <!doctype html>
    <html>
      <head>
        <meta charset="utf-8" />
        <title>invoice-${escapeHtml(order.orderNumber)}</title>
        <style>
          @page { margin: 4mm; size: 80mm auto; }
          body {
            font-family: "Courier New", monospace;
            margin: 0;
            color: #111827;
            font-size: 12px;
          }
          .bill {
            width: 72mm;
            margin: 0 auto;
          }
          .center { text-align: center; }
          .restaurant-name { font-size: 14px; font-weight: 700; margin: 6px 0 2px 0; }
          .logo {
            width: 82px;
            height: 82px;
            object-fit: contain;
            margin: 8px auto;
            display: block;
          }
          .header-block {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            margin: 2px 0 6px 0;
          }
          .header-left { text-align: left; line-height: 1.5; }
          .header-right { text-align: right; line-height: 1.5; white-space: nowrap; }
          .divider { border-top: 1px solid #111827; margin: 6px 0; }
          .section { margin-top: 10px; padding-top: 10px; border-top: 1px dashed #9ca3af; }
          .order-type {
            text-align: center;
            font-weight: bold;
            letter-spacing: 1px;
            margin: 8px 0 4px 0;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 8px;
          }
          th, td {
            padding: 6px 2px;
            vertical-align: top;
          }
          th { border-bottom: 1px solid #111827; font-size: 12px; }
          td:last-child, th:last-child { text-align: right; }
          .summary-row {
            display: flex;
            justify-content: space-between;
            margin: 4px 0;
          }
          .grand-total {
            font-weight: 800;
            font-size: 18px;
            margin-top: 6px;
          }
          .option-wrap { margin-top: 6px; }
          .opt-hdr { font-size: 11px; font-weight: 700; margin-top: 4px; }
          .opt-item { font-size: 12px; color: #374151; padding-left: 6px; }
          .opt-rm { color: #6b7280; }
          .footer { margin-top: 14px; text-align: center; font-size: 12px; }
        </style>
      </head>
      <body>
        <div class="bill">
          ${
            logoUrl
              ? `<img src="${escapeHtml(logoUrl)}" alt="Logo" class="logo" />`
              : ""
          }

          ${metadata.pickup_name ? `<div class="restaurant-name">${escapeHtml(metadata.pickup_name)}</div>` : ""}

          <div class="header-block">
            <div class="header-left">
              ${metadata.pickup_address ? `<div>${escapeHtml(metadata.pickup_address)}</div>` : ""}
              ${metadata.pickup_phone_number ? `<div>${escapeHtml(metadata.pickup_phone_number)}</div>` : ""}
            </div>
            <div class="header-right">
              <div>${escapeHtml(dateStr)}</div>
              <div>${escapeHtml(timeStr)}</div>
              ${order.employeeName ? `<div>${escapeHtml(order.employeeName)}</div>` : ""}
            </div>
          </div>

          <div class="divider"></div>

          <div>
            <div>Order #: ${escapeHtml(order.orderNumber)}</div>
            <div>Invoice: ${order.invoiceNumber ? escapeHtml(order.invoiceNumber) : escapeHtml(order._id.slice(-8).toUpperCase())}</div>
            ${
              order.tableToken !== undefined && order.tableToken !== null
                ? `<div>Table: ${escapeHtml(order.tableToken)}</div>`
                : ""
            }
            ${paymentMode ? `<div>Payment: ${escapeHtml(paymentMode)}</div>` : ""}
          </div>

          <div class="divider"></div>

          ${choice ? `<div class="order-type">${escapeHtml(choice.toUpperCase())}</div>` : ""}

          <div class="section">
            <table>
              <thead>
                <tr>
                  <th>Qty</th>
                  <th>Item</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                ${itemsMarkup}
              </tbody>
            </table>
          </div>

          <div class="section">
            ${summary.lines
              .map((line) => `<div class="summary-row"><span>${line.label}</span><span>${line.value}</span></div>`)
              .join("")}
            <div class="summary-row grand-total"><span>Total</span><span>${formatCurrency(summary.total)}</span></div>
          </div>

          <div class="footer">
            <div>Thank you for your order</div>
            <div>KEEP ORDERING!</div>
          </div>
        </div>
        <script>
          window.onload = function () {
            var imgs = Array.prototype.slice.call(document.images || []);
            var done = function () {
              setTimeout(function () {
                window.focus();
                window.print();
              }, 600);
            };
            if (!imgs.length) {
              done();
              return;
            }
            var loaded = 0;
            var tick = function () {
              loaded += 1;
              if (loaded >= imgs.length) done();
            };
            imgs.forEach(function (img) {
              if (img.complete) return tick();
              img.addEventListener("load", tick);
              img.addEventListener("error", tick);
            });
          };
          window.onafterprint = function () {
            window.close();
          };
        </script>
      </body>
    </html>
  `;

  billWindow.document.open();
  billWindow.document.write(html);
  billWindow.document.close();
};

export const printReceipt = (order: ReceiptOrder, logoUrl?: string, currencySymbol = cs()) => {
  generateOrderBillPdf({ order, logoUrl, currencySymbol });
};

const LINE_WIDTH = 48; // 80mm paper

/**
 * Sanitize user text for ESC/POS.
 * - NFC-normalize so decomposed accented chars (a + combining ◌̀) are precomposed
 *   into their single code point (à, é, ñ …) before anything else.
 * - Strip emojis: ESC/POS printers use CP437/CP850 encoding, not UTF-8. The
 *   multi-byte UTF-8 sequences for emojis get misread against the printer's CJK
 *   glyph table and appear as garbled Chinese characters.
 * - Replace bare ASCII control chars (which would confuse the printer's command
 *   parser) with a space.
 */
const escPos = (text: string): string =>
  text
    .normalize("NFC")
    .replace(/\p{Extended_Pictographic}/gu, "") // strip emojis (🌿 ❤️ etc.)
    .replace(/[\uFE0F\u200D\u20E3]/g, "")       // remove orphaned variation selectors & ZWJ
    .replace(/[\x00-\x1F\x7F]/g, " ")
    .replace(/\s{2,}/g, " ")
    .trim();

const wrapText = (text: string, width: number) => {
  const words = text.split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let current = "";

  words.forEach((word) => {
    if (!current.length) {
      current = word;
      return;
    }
    if (current.length + 1 + word.length <= width) {
      current += ` ${word}`;
      return;
    }
    lines.push(current);
    current = word;
  });

  if (current.length) lines.push(current);

  if (!lines.length) return [""];

  const normalized: string[] = [];
  lines.forEach((line) => {
    if (line.length <= width) {
      normalized.push(line);
      return;
    }
    for (let i = 0; i < line.length; i += width) {
      normalized.push(line.slice(i, i + width));
    }
  });

  return normalized;
};

const formatLine = (left: string, right: string, width = LINE_WIDTH) => {
  const spaceCount = width - left.length - right.length;
  if (spaceCount <= 1) return `${left} ${right}`.trimEnd();
  return `${left}${" ".repeat(spaceCount)}${right}`;
};

const pushThinGap = (lines: string[]) => {
  // Smaller-than-normal blank line for "in-between" spacing on ESC/POS.
  lines.push("\x1B\x33\x10"); // ESC 3 n: set line spacing (16 dots)
  lines.push("\n");
  lines.push("\x1B\x32"); // ESC 2: restore default line spacing
};

const buildItemLines = (item: ReceiptOrderItem): { text: string; bold: boolean }[] => {
  const lineTotal = Number(item.price || 0) * Number(item.quantity || 0);
  const priceStr = formatCurrency(lineTotal);
  const qtyPrefix = `${item.quantity}x `;
  // Reserve space for qty prefix + at least 1 space gap + price so price never overflows
  const nameWrapWidth = Math.max(
    LINE_WIDTH - qtyPrefix.length - priceStr.length - 1,
    16,
  );
  const nameLines = wrapText(escPos(item.name), nameWrapWidth);
  const output: { text: string; bold: boolean }[] = [];

  const cancelledLabel =
    item.itemstatus === "cancelled" ? " (Cancelled)" : "";

  const firstName = `${nameLines[0] || ""}${cancelledLabel}`;
  // First line (name + price): bold
  output.push({ text: formatLine(`${qtyPrefix}${firstName}`, priceStr), bold: true });

  // Wrapped name continuation lines: bold
  nameLines.slice(1).forEach((line) => {
    output.push({ text: `${" ".repeat(qtyPrefix.length)}${line}`, bold: true });
  });

  // Sectioned option display: [WHOLE] / [RIGHT HALF] / [LEFT HALF]
  const wholeESC: { label: string; removed: boolean }[] = [];
  const rightESC: { label: string; qty: number }[] = [];
  const leftESC:  { label: string; qty: number }[] = [];

  (item.customOptions ?? []).forEach((o) => {
    const m = o.label.match(/^(.*?)\s+(left|right|whole)$/i);
    const clean = escPos(m ? m[1] : o.label);
    const side  = m ? m[2].toLowerCase() : null;
    if (o.qty === -1)          wholeESC.push({ label: clean, removed: true });
    else if (side === "right") rightESC.push({ label: clean, qty: o.qty });
    else if (side === "left")  leftESC.push({ label: clean, qty: o.qty });
    else                       wholeESC.push({ label: clean, removed: false });
  });

  // Removed items: directly below item name, no section header
  const removedESC = wholeESC.filter((o) => o.removed);
  const addedWholeESC = wholeESC.filter((o) => !o.removed);
  removedESC.forEach(({ label }) => {
    wrapText(`NO ${label}`, LINE_WIDTH - 2).forEach((l) =>
      output.push({ text: `  ${l}`, bold: false }),
    );
  });
  if (addedWholeESC.length > 0) {
    const hasHalvesESC = leftESC.length > 0 || rightESC.length > 0;
    if (hasHalvesESC) output.push({ text: "  [WHOLE]", bold: false });
    addedWholeESC.forEach(({ label }) => {
      wrapText(label, LINE_WIDTH - 4).forEach((l) =>
        output.push({ text: `    ${l}`, bold: false }),
      );
    });
  }
  if (leftESC.length > 0) {
    output.push({ text: "  [LEFT HALF]", bold: false });
    leftESC.forEach(({ label, qty }) => {
      wrapText(qty > 1 ? `${qty}x ${label}` : label, LINE_WIDTH - 4).forEach((l) =>
        output.push({ text: `    ${l}`, bold: false }),
      );
    });
  }
  if (rightESC.length > 0) {
    output.push({ text: "  [RIGHT HALF]", bold: false });
    rightESC.forEach(({ label, qty }) => {
      wrapText(qty > 1 ? `${qty}x ${label}` : label, LINE_WIDTH - 4).forEach((l) =>
        output.push({ text: `    ${l}`, bold: false }),
      );
    });
  }

  return output;
};

// ── Timezone helpers (mirrors Kitchen.tsx logic) ─────────────────────────────

const DEFAULT_RECEIPT_TIMEZONE =
  (process.env.REACT_APP_ORDER_FALLBACK_TIMEZONE ?? "").trim() ||
  "America/Vancouver";

const isValidTimezone = (value?: string): value is string => {
  if (!value) return false;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: value }).format(new Date());
    return true;
  } catch {
    return false;
  }
};

const resolveReceiptTimezone = (order: ReceiptOrder): string => {
  const meta = order.metadata as Record<string, unknown> | undefined;
  const candidates = [
    order.scheduledTimezone,
    typeof meta?.scheduledTimezone === "string" ? meta.scheduledTimezone : undefined,
    typeof meta?.timeZone === "string" ? meta.timeZone : undefined,
    typeof meta?.timezone === "string" ? meta.timezone : undefined,
    typeof meta?.pickup_timezone === "string" ? meta.pickup_timezone : undefined,
    typeof meta?.dropoff_timezone === "string" ? meta.dropoff_timezone : undefined,
    DEFAULT_RECEIPT_TIMEZONE,
  ];
  return candidates.find(isValidTimezone) ?? DEFAULT_RECEIPT_TIMEZONE;
};

const formatKitchenTime = (dateTime: string | Date, tz: string): string => {
  const d = new Date(dateTime);
  if (Number.isNaN(d.getTime())) return "";
  try {
    return new Intl.DateTimeFormat("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      timeZone: tz,
      timeZoneName: "short",
    }).format(d);
  } catch {
    return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
  }
};

const formatKitchenScheduled = (dateTime: string | Date, tz: string): string => {
  const d = new Date(dateTime);
  if (Number.isNaN(d.getTime())) return "";
  try {
    return new Intl.DateTimeFormat("en-US", {
      month: "2-digit",
      day: "2-digit",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      timeZone: tz,
      timeZoneName: "short",
    }).format(d);
  } catch {
    return d.toLocaleString();
  }
};

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Builds ESC/POS bytes for kitchen tickets.
 * One ticket per item — each has a bold ORDER # header and ends with a partial cut.
 * Order: 3 items → 3 tickets → 3 cuts.
 */
export const buildKitchenTicketsLines = (order: ReceiptOrder): string[] => {
  const choice = order.choice || "";
  const tz = resolveReceiptTimezone(order);
  const isScheduled = !!(order.isScheduled && order.scheduledAt);
  const timeStr = isScheduled ? formatKitchenScheduled(order.scheduledAt!, tz) : "";

  // Order creation date/time shown in header row
  const createdDateStr = new Intl.DateTimeFormat("en-US", {
    month: "long", day: "numeric", year: "numeric", timeZone: tz,
  }).format(new Date(order.createdAt));
  const createdTimeStr = formatKitchenTime(order.createdAt, tz);

const allLines: string[] = [];

  order.items.forEach((item, idx) => {
    const lines: string[] = [];

    // ESC @ (init) only on the very first ticket.
    // Putting it on subsequent tickets risks cancelling the previous cut command
    // before the printer has physically executed it.
    if (idx === 0) {
      lines.push("\x1B\x40");               // ESC @ — init
      lines.push("\x1B\x37\x05\x40\x01");   // heat settings
      lines.push("\x1D\x7C\x04");           // density
    }

    // Top white margin
    lines.push("\x0A\x0A");

    // Safe format-reset for every ticket (does NOT clear print buffer)
    lines.push("\x1B\x61\x00");             // left align
    lines.push("\x1B\x45\x00\x1B\x47\x00"); // bold off
    lines.push("\x1D\x21\x00");             // normal size

    // ── ORDER # — centered, double width + double height ─────────────────────
    lines.push("\x1B\x61\x01");
    lines.push("\x1B\x45\x01\x1B\x47\x01");
    lines.push("\x1D\x21\x11");
    lines.push(`ORDER #${order.orderNumber}\n`);
    lines.push("\x1D\x21\x00");
    lines.push("\x1B\x45\x00\x1B\x47\x00");
    lines.push("\x1B\x61\x00");
    lines.push(`${"-".repeat(LINE_WIDTH)}\n`);

    // ── Date (left) + Creation time (right) ──────────────────────────────────
    lines.push(formatLine(createdDateStr, createdTimeStr) + "\n");
    if (order.tableToken !== undefined && order.tableToken !== null) {
      lines.push(`Table: ${order.tableToken}\n`);
    }
    if (order.employeeName) lines.push(`Server: ${escPos(order.employeeName)}\n`);
    lines.push(`${"-".repeat(LINE_WIDTH)}\n`);

    // ── Order type + scheduled time — centered, bold (like PICKUP 9:45 PM PDT) ─
    lines.push("\x1B\x61\x01");
    lines.push("\x1B\x45\x01\x1B\x47\x01");
    const centerLabel = choice ? escPos(choice).toUpperCase() : "ORDER";
    lines.push("\x1D\x21\x01"); // slightly larger pickup/order-type text
    lines.push(isScheduled ? `${centerLabel} ${timeStr}\n` : `${centerLabel}\n`);
    lines.push("\x1D\x21\x00");
    lines.push("\x1B\x45\x00\x1B\x47\x00");
    lines.push("\x1B\x61\x00");
    lines.push(`${"-".repeat(LINE_WIDTH)}\n`);

    // ── Item name — normal width, bold, increased char spacing ───────────────
    if (item.itemstatus === "cancelled") {
      lines.push("\x1B\x45\x01\x1B\x47\x01");
      lines.push("*** CANCELLED ***\n");
      lines.push("\x1B\x45\x00\x1B\x47\x00");
    }
    lines.push("\x1B\x45\x01\x1B\x47\x01"); // bold + double-strike
    lines.push("\x1B\x20\x02");             // +2 dots character spacing
    const qtyPfx = `${item.quantity} \u00D7 `;
    const nameWrapW2 = LINE_WIDTH - qtyPfx.length;
    lines.push("\x1D\x21\x01");             // reduced from very large size
    const nameWrappedK = wrapText(escPos(item.name), nameWrapW2);
    nameWrappedK.forEach((l, i) =>
      lines.push(`${i === 0 ? qtyPfx : " ".repeat(qtyPfx.length)}${l}\n`),
    );
    lines.push("\x1D\x21\x00");
    lines.push("\x1B\x20\x00");             // reset character spacing
    lines.push("\x1B\x45\x00\x1B\x47\x00"); // bold off

    // ── Custom options ────────────────────────────────────────────────────────
    const wholeK: { label: string; removed: boolean }[] = [];
    const rightK: { label: string; qty: number }[] = [];
    const leftK:  { label: string; qty: number }[] = [];
    (item.customOptions ?? []).forEach((o) => {
      const m = o.label.match(/^(.*?)\s+(left|right|whole)$/i);
      const clean = escPos(m ? m[1] : o.label);
      const side  = m ? m[2].toLowerCase() : null;
      if (o.qty === -1)          wholeK.push({ label: clean, removed: true });
      else if (side === "right") rightK.push({ label: clean, qty: o.qty });
      else if (side === "left")  leftK.push({ label: clean, qty: o.qty });
      else                       wholeK.push({ label: clean, removed: false });
    });

    const removedK    = wholeK.filter((o) => o.removed);
    const addedWholeK = wholeK.filter((o) => !o.removed);

    if (removedK.length > 0 || addedWholeK.length > 0 || leftK.length > 0 || rightK.length > 0) {
      pushThinGap(lines);
      lines.push("\x1D\x21\x00"); // customization text 1 step smaller
      lines.push("\x1B\x45\x01\x1B\x47\x00"); // normal size, slightly heavier
    }
    removedK.forEach(({ label }) =>
      {
        wrapText(`NO ${label}`, LINE_WIDTH - 2).forEach((l) =>
          lines.push(`  ${l}\n`),
        );
      },
    );
    if (addedWholeK.length > 0) {
      if (removedK.length > 0) pushThinGap(lines);
      if (leftK.length > 0 || rightK.length > 0) lines.push("  [WHOLE]\n");
      addedWholeK.forEach(({ label }) =>
        {
          wrapText(label, LINE_WIDTH - 4).forEach((l, i) =>
            lines.push(`${i === 0 ? "  \u00B7 " : "    "}${l}\n`),
          );
        },
      );
    }
    if (leftK.length > 0) {
      if (removedK.length > 0 || addedWholeK.length > 0) pushThinGap(lines);
      lines.push("  [LEFT HALF]\n");
      leftK.forEach(({ label, qty }) =>
        {
          wrapText(qty > 1 ? `${qty}x ${label}` : label, LINE_WIDTH - 4).forEach((l, i) =>
            lines.push(`${i === 0 ? "  \u00B7 " : "    "}${l}\n`),
          );
        },
      );
    }
    if (rightK.length > 0) {
      if (removedK.length > 0 || addedWholeK.length > 0 || leftK.length > 0) pushThinGap(lines);
      lines.push("  [RIGHT HALF]\n");
      rightK.forEach(({ label, qty }) =>
        {
          wrapText(qty > 1 ? `${qty}x ${label}` : label, LINE_WIDTH - 4).forEach((l, i) =>
            lines.push(`${i === 0 ? "  \u00B7 " : "    "}${l}\n`),
          );
        },
      );
    }
    if (removedK.length > 0 || addedWholeK.length > 0 || leftK.length > 0 || rightK.length > 0) {
      lines.push("\x1B\x45\x00\x1B\x47\x00");
      lines.push("\x1D\x21\x00");
    }

    // 6 LF then partial cut
    lines.push("\x0A\x0A\x0A\x0A\x0A\x0A"); // 6 LF — gap so last line clears the cutter
    lines.push("\x1D\x56\x01"); // GS V 1 — partial cut

    allLines.push(...lines);
  });

  return allLines;
};

/**
 * Same as buildKitchenTicketsLines but returns one string[] per item.
 * Used by the BLE printer to send each ticket as a separate write with
 * a pause between them so the printer can physically execute the cut.
 * Each ticket gets a full ESC @ init since they are independent transmissions.
 */
export const buildKitchenTicketsList = (order: ReceiptOrder): string[][] => {
  const choice = order.choice || "";
  const tz = resolveReceiptTimezone(order);
  const isScheduled = !!(order.isScheduled && order.scheduledAt);
  const metadata = order.metadata as Record<string, unknown> | undefined;
  const createdDateStr = new Intl.DateTimeFormat("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
    timeZone: tz,
  }).format(new Date(order.createdAt));
  const createdTimeStr = formatKitchenTime(order.createdAt, tz);
  const pickupTimeStr = isScheduled
    ? formatKitchenTime(order.scheduledAt!, tz)
    : createdTimeStr;

  const getMetaValue = (...keys: string[]): string => {
    for (const key of keys) {
      const value = metadata?.[key];
      if (typeof value === "string" && value.trim()) return value.trim();
    }
    return "";
  };

  const customerName = getMetaValue(
    "dropoff_name",
    "customer_name",
    "customerName",
    "name",
    "user_name",
    "userName",
  );
  const sourceLine = [
    choice ? `${escPos(choice)} Order` : "",
    // order.phoneNumber ? String(order.phoneNumber).trim() : "",
  ]
    .filter(Boolean)
    .join(" ");
  const kitchenNote = getMetaValue("dropoff_notes", "notes", "note", "kitchen_note");
  const printableItems = order.items.filter((item) => item.itemstatus !== "cancelled");
  const itemsForTickets = printableItems.length ? printableItems : [null];

  return itemsForTickets.map((item) => {
    const lines: string[] = [];

    // Full init per ticket so each item prints and cuts separately.
    lines.push("\x1B\x40");               // ESC @ — init
    lines.push("\x1B\x37\x05\x40\x01");   // heat settings
    lines.push("\x1D\x7C\x04");           // density
    lines.push("\x0A\x0A\x0A");           // top white margin

    lines.push("\x1B\x4D\x00");           // font A
    lines.push("\x1B\x61\x01");           // center
    lines.push("\x1B\x45\x01\x1B\x47\x01");
    lines.push("\x1D\x21\x11");           // double width + double height
    lines.push(`ORDER #${order.orderNumber}\n`);
    lines.push("\x1D\x21\x00");
    lines.push("\x1B\x45\x00\x1B\x47\x00");

    lines.push("\x1B\x61\x00");           // left
    if (sourceLine) lines.push(`${sourceLine}\n`);
    lines.push(`${"-".repeat(LINE_WIDTH)}\n`);

    lines.push(formatLine(createdDateStr, createdTimeStr) + "\n");
    if (customerName) lines.push(`${escPos(customerName)}\n`);
    if (order.tableToken !== undefined && order.tableToken !== null) {
      lines.push(`Table ${order.tableToken}\n`);
    }
    lines.push(`${"-".repeat(LINE_WIDTH)}\n`);

    lines.push("\x1B\x61\x01");
    lines.push("\x1B\x45\x01\x1B\x47\x01");
    lines.push("\x1D\x21\x01"); // slightly larger pickup text
    lines.push(`PICKUP ${pickupTimeStr}\n`);
    lines.push("\x1D\x21\x00");
    lines.push("\x1B\x45\x00\x1B\x47\x00");
    lines.push("\x1B\x61\x00");
    lines.push(`${"-".repeat(LINE_WIDTH)}\n`);

    if (!item) {
      lines.push("(No active items)\n");
    } else {
      // Item name: wider text, lighter boldness, and keep on one line when it fits.
      lines.push("\x1B\x4D\x00");         // font A
      lines.push("\x1B\x45\x01\x1B\x47\x01"); // strong bold item name
      lines.push("\x1B\x20\x01");         // slight char spacing
      lines.push("\x1D\x21\x01");         // reduced item-name size
      const itemLabel = `${item.quantity} x ${escPos(item.name)}`;
      const itemWidth = LINE_WIDTH - 1;
      const wrappedItem = wrapText(itemLabel, itemWidth);
      if (wrappedItem.length === 1) lines.push(`${wrappedItem[0]}\n`);
      else wrappedItem.forEach((l) => lines.push(`${l}\n`));
      lines.push("\x1D\x21\x00");
      lines.push("\x1B\x20\x00");
      lines.push("\x1B\x45\x00\x1B\x47\x00");

      // Keep original LEFT/RIGHT/WHOLE option grouping behavior.
      lines.push("\x1D\x21\x00");         // customization text 1 step smaller
      lines.push("\x1B\x45\x01\x1B\x47\x00"); // normal size, slightly heavier
      lines.push("\x1B\x4D\x00");         // font A
      const wholeK: { label: string; removed: boolean }[] = [];
      const rightK: { label: string; qty: number }[] = [];
      const leftK: { label: string; qty: number }[] = [];
      (item.customOptions ?? []).forEach((o) => {
        const m = o.label.match(/^(.*?)\s+(left|right|whole)$/i);
        const clean = escPos(m ? m[1] : o.label);
        const side = m ? m[2].toLowerCase() : null;
        if (o.qty === -1) wholeK.push({ label: clean, removed: true });
        else if (side === "right") rightK.push({ label: clean, qty: o.qty });
        else if (side === "left") leftK.push({ label: clean, qty: o.qty });
        else wholeK.push({ label: clean, removed: false });
      });
      const removedK = wholeK.filter((o) => o.removed);
      const addedWholeK = wholeK.filter((o) => !o.removed);

      pushThinGap(lines);
      removedK.forEach(({ label }) =>
        {
          wrapText(`NO ${label}`, LINE_WIDTH - 2).forEach((l) =>
            lines.push(`  ${l}\n`),
          );
        },
      );
      if (addedWholeK.length > 0) {
        if (removedK.length > 0) pushThinGap(lines);
        lines.push("  [WHOLE]\n");
        lines.push("\x1B\x61\x01"); // center align
        addedWholeK.forEach(({ label }) =>
          {
            wrapText(label, LINE_WIDTH).forEach((l) => lines.push(`${l}\n`));
          },
        );
        lines.push("\x1B\x61\x00"); // reset left
      }
      if (leftK.length > 0) {
        if (removedK.length > 0 || addedWholeK.length > 0) pushThinGap(lines);
        lines.push("  [LEFT HALF]\n");
        leftK.forEach(({ label, qty }) =>
          {
            wrapText(qty > 1 ? `${qty}x ${label}` : label, LINE_WIDTH - 4).forEach(
              (l) => lines.push(`    ${l}\n`),
            );
          },
        );
      }
      if (rightK.length > 0) {
        if (removedK.length > 0 || addedWholeK.length > 0 || leftK.length > 0) pushThinGap(lines);
        lines.push("  [RIGHT HALF]\n");
        lines.push("\x1B\x61\x02"); // right align
        rightK.forEach(({ label, qty }) =>
          {
            wrapText(qty > 1 ? `${qty}x ${label}` : label, LINE_WIDTH).forEach(
              (l) => lines.push(`${l}\n`),
            );
          },
        );
        lines.push("\x1B\x61\x00"); // reset left
      }
      lines.push("\x1B\x45\x00\x1B\x47\x00");
      lines.push("\x1D\x21\x00");
    }

    if (kitchenNote) {
      lines.push("\n");
      lines.push(`${"-".repeat(LINE_WIDTH)}\n`);
      wrapText(escPos(kitchenNote), LINE_WIDTH).forEach((l) =>
        lines.push(`${l}\n`),
      );
    }

    lines.push("\n");
    lines.push(`${"-".repeat(LINE_WIDTH)}\n`);
    lines.push(`Receipt #${order._id.slice(-4).toUpperCase()}\n`);
    lines.push("\x0A\x0A\x0A\x0A\x0A\x0A");
    lines.push("\x1D\x56\x01"); // partial cut

    return lines;
  });
};

export const buildEscPosReceipt = (
  order: ReceiptOrder,
  _customerName?: string,
  currencySymbol = cs(),
) => {
  // Update module-level formatter so buildItemLines picks it up
  formatCurrency = makeCurrencyFormatter(currencySymbol);
  const metadata = order.metadata || {};
  const paymentMode = order.payChoice || "";
  const choice = order.choice || "";
  const tz = resolveReceiptTimezone(order);
  const dateStr = new Intl.DateTimeFormat("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
    timeZone: tz,
  }).format(new Date(order.createdAt));
  const timeStr = formatKitchenTime(order.createdAt, tz);

  const summary = buildReceiptSummary(order, formatCurrency);

  const lines: string[] = [];
  lines.push("\x1B\x40"); // initialize
  lines.push("\x1B\x37\x05\x40\x01"); // ESC 7 — heat: dots=5, time=0x40(64×10µs), interval=1
  lines.push("\x1D\x7C\x04"); // GS | density 4 (0=lightest, 8=darkest)
  lines.push("\x1B\x61\x00"); // left align

  // Header block: left = name (bold) then address/phone; right = date/time/employee
  // All rows share the same two-column layout so name and date are parallel (same lines).
  const rightLines: string[] = [dateStr, timeStr];
  if (order.employeeName) rightLines.push(escPos(order.employeeName));

  const maxRightLen = rightLines.reduce((m, r) => Math.max(m, r.length), 0);
  // Wrap name to leave room for the right column
  const nameWrapW = Math.max(LINE_WIDTH - maxRightLen - 1, 16);
  const nameLeftLines = metadata.pickup_name
    ? wrapText(escPos(metadata.pickup_name), nameWrapW)
    : [];

  const addressLeftLines: string[] = [];
  if (metadata.pickup_address) addressLeftLines.push(escPos(metadata.pickup_address));
  if (metadata.pickup_phone_number) addressLeftLines.push(escPos(metadata.pickup_phone_number));

  const leftLines = [...nameLeftLines, ...addressLeftLines];
  const maxRows = Math.max(leftLines.length, rightLines.length);

  for (let i = 0; i < maxRows; i++) {
    const left = leftLines[i] || "";
    const right = rightLines[i] || "";
    const isBold = i < nameLeftLines.length;
    const spaces = Math.max(1, LINE_WIDTH - left.length - right.length);

    if (isBold) {
      // Bold + double-strike for stronger visual contrast
      lines.push(`\x1B\x45\x01\x1B\x47\x01${left}\x1B\x45\x00\x1B\x47\x00${right ? " ".repeat(spaces) + right : ""}\n`);
    } else if (right) {
      lines.push(formatLine(left, right) + "\n");
    } else if (left) {
      lines.push(`${left}\n`);
    }
  }

  lines.push(`${"-".repeat(LINE_WIDTH)}\n`);

  lines.push(`Order #: ${order.orderNumber}\n`);
  lines.push(`Invoice: ${order.invoiceNumber ?? order._id.slice(-8).toUpperCase()}\n`);
  if (order.tableToken !== undefined && order.tableToken !== null) {
    lines.push(`Table: ${order.tableToken}\n`);
  }
  if (paymentMode) lines.push(`Payment: ${escPos(paymentMode)}\n`);

  lines.push(`${"-".repeat(LINE_WIDTH)}\n`);

  if (choice) {
    lines.push("\x1B\x61\x01"); // center
    lines.push(`${escPos(choice).toUpperCase()}\n`);
    lines.push("\x1B\x61\x00"); // left
    lines.push(`${"-".repeat(LINE_WIDTH)}\n`);
  }

  lines.push(formatLine("Qty Item", "Total") + "\n");
  lines.push(`${"-".repeat(LINE_WIDTH)}\n`);

  order.items.forEach((item) => {
    buildItemLines(item).forEach(({ text, bold }) => {
      if (bold) {
        lines.push(`\x1B\x45\x01\x1B\x47\x01${text}\x1B\x45\x00\x1B\x47\x00\n`);
      } else {
        lines.push(`${text}\n`);
      }
    });
  });

  lines.push(`${"-".repeat(LINE_WIDTH)}\n`);
  summary.lines.forEach((line) => {
    lines.push(formatLine(line.label, line.value) + "\n");
  });
  lines.push(`${"-".repeat(LINE_WIDTH)}\n`);

  // Total — bold + double-strike
  lines.push(`\x1B\x45\x01\x1B\x47\x01${formatLine("Total", formatCurrency(summary.total))}\x1B\x45\x00\x1B\x47\x00\n`);

  lines.push(`${"-".repeat(LINE_WIDTH)}\n`);
  lines.push("\x1B\x61\x01"); // center
  lines.push("Thank you for your order\n");
  lines.push("KEEP ORDERING!\n");
  lines.push("\x1B\x61\x00");
  lines.push("\n\n\n");
  lines.push("\x1D\x56\x41\x03"); // partial cut

  return lines;
};
